// EarningsPage — 服务商收益 + 提现（i18n：en/zh，文案在 i18n.jsx 的 earnings.* 键）。
//  顶部：提现动作卡（可提现收益 + 提现按钮/表单），始终可见，不藏进 tab。
//  下方 tabs 只划分「数据信息」：收益明细（每笔分成）与提现记录（历史提现）。
// 可提现 = earningsBalance（已结算收益 − 已提/锁定），独立于充值/赠送 balance。
// 仅服务商可见（非服务商 overview=null → 引导发布 API）。

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { isAddress, getAddress } from 'viem';
import { XPrim } from '../primitives.jsx';
import { XShell } from '../shell.jsx';
import { XApi } from '../services.js';
import { useT } from '../i18n.jsx';

const usd = (n) => '$' + (Number(n) || 0).toFixed(2);
// 单笔收益常见 <$0.01（按次分成常摊到几厘），toFixed(2) 会全展示成 $0.00 —— 改用
// 「前导零个数下标」记法（如 0.00016 → $0.0₃16，保留 2 位有效数字），$0.01 以上退化回普通两位小数。
const SUB_DIGITS = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉'];
const toSub = (n) => String(n).split('').map((d) => SUB_DIGITS[+d]).join('');
const usdSmart = (n) => {
  const v = Number(n) || 0;
  if (v === 0) return '$0.00';
  const sign = v < 0 ? '-' : '';
  const abs = Math.abs(v);
  if (abs >= 0.01) return `${sign}$${abs.toFixed(2)}`;
  const [mantissa, exp] = abs.toExponential(1).split('e');
  const zeros = -Number(exp) - 1;
  return `${sign}$0.0${toSub(zeros)}${mantissa.replace('.', '')}`;
};
const short = (s, n = 6) => (s ? `${s.slice(0, n)}…${s.slice(-4)}` : '—');
// EVM 地址校验：viem 的 isAddress 会做 EIP-55 校验和检查（全小写/全大写/正确校验和都接受，
// 但大小写混合且校验和错误的会被拒——能挡住手抄错一位的地址）。
const isAddr = (s) => { try { return isAddress(String(s || '').trim(), { strict: false }); } catch { return false; } };
const toChecksum = (s) => { try { return getAddress(String(s || '').trim()); } catch { return String(s || '').trim(); } };
const fmtDate = (d) => { try { return new Date(d).toLocaleDateString(); } catch { return '—'; } };

// status → { i18n key, color }。label 在 Pill 里用 t(k) 解析。
const WD_STATUS = {
  REQUESTED: { k: 'earnings.wd.REQUESTED', c: 'var(--fg-2)' },
  APPROVED: { k: 'earnings.wd.APPROVED', c: '#e0a93f' },
  QUEUED: { k: 'earnings.wd.QUEUED', c: '#3fa9e0' },
  BROADCAST: { k: 'earnings.wd.BROADCAST', c: '#3fa9e0' },
  CONFIRMED: { k: 'earnings.wd.CONFIRMED', c: 'var(--c-mint, #5fd0a8)' },
  REJECTED: { k: 'earnings.wd.REJECTED', c: 'var(--err, #f55)' },
  FAILED: { k: 'earnings.wd.FAILED', c: 'var(--err, #f55)' },
  CANCELED: { k: 'earnings.wd.CANCELED', c: 'var(--fg-4)' },
};
const EARN_STATUS = {
  PENDING: { k: 'earnings.earn.PENDING', c: '#e0a93f' },
  SETTLED: { k: 'earnings.earn.SETTLED', c: 'var(--c-mint, #5fd0a8)' },
};
const scanUrl = (network, tx) =>
  `https://${network === 'base' ? '' : 'sepolia.'}basescan.org/tx/${tx}`;

const Pill = ({ map, status }) => {
  const { t } = useT();
  const s = map[status] || { k: null, c: 'var(--fg-2)' };
  return (
    <span className="mono" style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, color: s.c, border: `1px solid ${s.c}`, whiteSpace: 'nowrap' }}>{s.k ? t(s.k) : status}</span>
  );
};

export const EarningsPage = () => {
  const { navigate } = XShell.useRoute();
  const { authed, user, refreshMe } = XShell.useAuth();
  const scope = authed ? (user?._raw?.id ?? user?.id ?? null) : null;
  const identity = useRef({ scope, version: 0 });
  if (identity.current.scope !== scope) identity.current = { scope, version: identity.current.version + 1 };
  const requestId = useRef(0);
  const [loadedScope, setLoadedScope] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const { toast } = XPrim.useToast();
  const { t } = useT();

  // 安全网：进入本页时释放可能被弹层卡住的 body 滚动锁，保证能上下滚动。
  useEffect(() => {
    if (document.body.style.overflow === 'hidden') document.body.style.overflow = '';
  }, []);

  const [tab, setTab] = useState('detail'); // 'detail' | 'records'
  const [overview, setOverview] = useState(null);
  const [earnings, setEarnings] = useState(null); // { items, total, totalNet, ... }
  const [cfg, setCfg] = useState(null);            // 提现规则 { minUsd, maxUsd, dailyCapUsd, ... }
  const [items, setItems] = useState([]);          // withdrawals
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [amount, setAmount] = useState('');
  const [toAddress, setToAddress] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false); // 二次确认面板
  // 收益划转（earnings → 消费余额）子面板状态
  const [tferShow, setTferShow] = useState(false);
  const [tferAmount, setTferAmount] = useState('');
  const [tferSubmitting, setTferSubmitting] = useState(false);
  const [tferConfirming, setTferConfirming] = useState(false);
  const [tferCfg, setTferCfg] = useState(null); // 划转规则 { minUsd }

  // Refresh the authoritative profile after a new earnings snapshot or a
  // successful withdrawal/transfer reload. Never copy provider totals into
  // the independent consumer balance. Older /me reads are fenced by AuthProvider.
  useEffect(() => {
    if (overview) void refreshMe?.();
  }, [overview, refreshMe]);

  const reload = useCallback(async () => {
    if (!scope) { setLoading(false); return; }
    setLoadError(false);
    const version = identity.current.version;
    const ticket = ++requestId.current;
    const current = () => identity.current.version === version && requestId.current === ticket;
    try {
      const [ov, earn, list, conf, tconf] = await Promise.all([
        XApi.providerDashboardService.overview(),
        XApi.providerDashboardService.earningsHistory({ page: 1, limit: 20 }),
        XApi.withdrawalService.listMine(),
        XApi.withdrawalService.config(),
        XApi.earningsTransferService.config(),
      ]);
      if (!current()) return;
      setLoadedScope(scope);
      setOverview(ov);
      setEarnings(earn);
      setItems(list);
      if (conf) setCfg(conf);
      if (tconf) setTferCfg(tconf);
    } catch (e) {
      if (current()) { setLoadedScope(scope); setLoadError(true); }
    } finally { if (current()) setLoading(false); }
  }, [scope]);

  useEffect(() => {
    setOverview(null); setEarnings(null); setItems([]); setCfg(null); setTferCfg(null); setLoadedScope(null);
    setShowForm(false); setConfirming(false); setAmount(''); setToAddress(''); setSubmitting(false);
    setTferShow(false); setTferConfirming(false); setTferAmount(''); setTferSubmitting(false);
    setLoading(!!scope); reload();
    return () => { requestId.current++; };
  }, [reload, scope]);

  // 出款中的提现 → 轮询直到终态
  useEffect(() => {
    const active = items.some((w) => ['APPROVED', 'QUEUED', 'BROADCAST'].includes(w.status));
    if (!active || loadedScope !== scope) return;
    let disposed = false;
    const version = identity.current.version;
    const id = setInterval(() => { XApi.withdrawalService.listMine().then(rows => {
      if (!disposed && identity.current.version === version) setItems(rows);
    }).catch(() => {}); }, 6000);
    return () => { disposed = true; clearInterval(id); };
  }, [items, scope, loadedScope]);

  // Navigation and the actionable wallet balance share the same authoritative
  // /me snapshot, including zero. Provider totals remain their own ledger view.
  const withdrawable = Number(user?._raw?.earningsBalance != null
    ? user.earningsBalance : (overview?.withdrawableBalance || 0));
  const minUsd = Number(cfg?.minUsd || 0);
  const maxUsd = Number(cfg?.maxUsd || 0);
  const amtNum = Number(amount);
  const addrTrim = String(toAddress || '').trim();
  const addrValid = isAddr(addrTrim);
  // 金额校验信息（用于内联提示 + 是否可进入二次确认）
  const amountErr =
    amount === '' ? null
    : !(amtNum > 0) ? t('earnings.err.amountGt0')
    : (minUsd && amtNum < minUsd) ? t('earnings.err.minPerTx', { amt: usd(minUsd) })
    : (maxUsd && amtNum > maxUsd) ? t('earnings.err.maxPerTx', { amt: usd(maxUsd) })
    : (amtNum > withdrawable) ? t('earnings.err.exceed')
    : null;
  const canConfirm = amtNum > 0 && !amountErr && addrValid;
  const networkLabel = cfg?.network === 'base' ? t('earnings.network.baseMainnet')
    : cfg?.network === 'base-sepolia' ? t('earnings.network.baseSepolia')
    : (cfg?.network || 'Base');

  // 第一步：校验后进入二次确认（不直接提交）
  const goConfirm = () => {
    if (!(amtNum > 0)) { toast({ title: t('earnings.toast.enterAmount'), type: 'error' }); return; }
    if (amountErr) { toast({ title: amountErr, type: 'error' }); return; }
    if (!addrValid) { toast({ title: t('earnings.toast.invalidAddr'), type: 'error' }); return; }
    setConfirming(true);
  };

  // 第二步：用户在确认面板核对后真正提交
  const confirmSubmit = async () => {
    const version = identity.current.version;
    const current = () => identity.current.version === version;
    setSubmitting(true);
    try {
      await XApi.withdrawalService.create({ amount: amtNum, toAddress: toChecksum(addrTrim) });
      if (!current()) return;
      toast({ title: t('earnings.toast.submitted'), type: 'success' });
      setShowForm(false); setConfirming(false); setAmount(''); setToAddress('');
      setTab('records');
      await reload();
    } catch (e) {
      if (!current()) return;
      toast({ title: e?.data?.message || e?.message || t('earnings.toast.submitFail'), type: 'error' });
    } finally { if (current()) setSubmitting(false); }
  };

  const cancel = async (id) => {
    const version = identity.current.version;
    const current = () => identity.current.version === version;
    try {
      await XApi.withdrawalService.cancel(id);
      if (!current()) return;
      toast({ title: t('earnings.toast.canceled'), type: 'success' });
      await reload();
    } catch (e) { if (!current()) return; toast({ title: e?.data?.message || t('earnings.toast.cancelFail'), type: 'error' }); }
  };

  // ---- 收益划转（earnings → 消费余额）----
  const tferMin = Number(tferCfg?.minUsd || 0);
  const tferAmtNum = Number(tferAmount);
  const tferErr =
    tferAmount === '' ? null
    : !(tferAmtNum > 0) ? '金额必须大于 0'
    : (tferMin && tferAmtNum < tferMin) ? `单笔最低 ${usd(tferMin)}`
    : (tferAmtNum > withdrawable) ? '超出可划转收益'
    : null;
  const tferCanConfirm = tferAmtNum > 0 && !tferErr;

  const tferGoConfirm = () => {
    if (!(tferAmtNum > 0)) { toast({ title: '请输入划转金额', type: 'error' }); return; }
    if (tferErr) { toast({ title: tferErr, type: 'error' }); return; }
    setTferConfirming(true);
  };
  const tferSubmit = async () => {
    const version = identity.current.version;
    const current = () => identity.current.version === version;
    setTferSubmitting(true);
    try {
      await XApi.earningsTransferService.create({ amount: tferAmtNum });
      if (!current()) return;
      toast({ title: '已划转到消费余额', type: 'success' });
      setTferShow(false); setTferConfirming(false); setTferAmount('');
      await reload();
    } catch (e) {
      if (!current()) return;
      toast({ title: e?.data?.message || e?.message || '划转失败', type: 'error' });
    } finally { if (current()) setTferSubmitting(false); }
  };

  // ---- gating ----
  if (!authed) {
    return <Shell><Gate title={t('earnings.gate.loginTitle')} cta={t('earnings.gate.loginCta')} onClick={() => navigate('login')} /></Shell>;
  }
  if (loading || !scope || loadedScope !== scope) {
    return <Shell><div style={{ color: 'var(--fg-3)', padding: 40, textAlign: 'center' }}>{t('earnings.loading')}</div></Shell>;
  }
  if (loadError) {
    return <Shell><div role="alert">{t('earnings.loadError')} <button className="btn" onClick={() => { setLoading(true); reload(); }}>{t('common.retry')}</button></div></Shell>;
  }
  if (!overview) {
    return (
      <Shell>
        <Gate title={t('earnings.gate.notProviderTitle')} desc={t('earnings.gate.notProviderDesc')} cta={t('earnings.gate.notProviderCta')} onClick={() => navigate('console')} />
      </Shell>
    );
  }

  const feePct = Math.round(Number(overview.platformFeeRate || 0) * 100);
  const pending = Number(overview.pendingEarnings || 0);
  const blocked = !!overview.withdrawalBlocked;            // 被管理员拉黑 → 禁止提现
  const blockedReason = overview.withdrawalBlockedReason;
  const tg = cfg?.supportTelegram;                          // 联系管理员的电报群

  return (
    <Shell>
      {/* ===== 提现动作卡：始终可见，不藏进 tab ===== */}
      <div className="card" style={{ padding: 20, marginBottom: 22, border: `1px solid ${blocked ? 'rgba(245,85,85,0.35)' : 'rgba(95,208,168,0.25)'}` }}>
        {blocked && (
          <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(245,85,85,0.08)', border: '1px solid rgba(245,85,85,0.35)', marginBottom: 16, fontSize: 13, color: '#f08a8a', lineHeight: 1.7 }}>
            <b>{t('earnings.blocked.title')}</b>{blockedReason ? t('earnings.blocked.reasonSep', { reason: blockedReason }) : ''}{t('earnings.blocked.body')}
            {tg && (
              <> <a href={tg} target="_blank" rel="noreferrer" style={{ color: '#f08a8a', textDecoration: 'underline' }}>{t('earnings.blocked.contact')}</a></>
            )}
          </div>
        )}
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div className="row" style={{ gap: 40, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div>
              <div className="micro-mono" style={{ color: 'var(--fg-4)', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 11 }}>{t('earnings.withdrawable')}</div>
              <div className="mono mint-text" style={{ fontSize: 38, fontWeight: 700, lineHeight: 1.05, marginTop: 4 }}>{usd(withdrawable)}</div>
            </div>
            <div>
              <div className="micro-mono" style={{ color: 'var(--fg-4)', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 11 }}>{t('earnings.totalNet')}</div>
              <div className="mono" style={{ fontSize: 38, fontWeight: 700, lineHeight: 1.05, marginTop: 4, color: 'var(--fg-0)' }}>{usd(overview.totalNetEarnings)}</div>
            </div>
          </div>
          <div className="row" style={{ gap: 8 }}>
            {/* 划转到消费余额：不受提现拉黑限制（内部划转非链上出款），仅需有可划转收益 */}
            <button className="btn" disabled={withdrawable <= 0} onClick={() => { setShowForm(false); setConfirming(false); setTferConfirming(false); setTferShow((v) => !v); }} style={{ fontSize: 14, padding: '9px 18px' }} title="把收益转入消费余额，用于调用 API（不可提现）">
              {tferShow ? '收起' : '划转到余额'}
            </button>
            <button className="btn btn-primary" disabled={withdrawable <= 0 || blocked} onClick={() => { setTferShow(false); setConfirming(false); setShowForm((v) => !v); }} style={{ fontSize: 14, padding: '9px 22px' }} title={blocked ? t('earnings.btn.blockedTitle') : undefined}>
              {blocked ? t('earnings.btn.blocked') : showForm ? t('earnings.btn.collapse') : t('earnings.btn.withdraw')}
            </button>
          </div>
        </div>
        <div className="micro-mono" style={{ color: 'var(--fg-4)', fontSize: 11, marginTop: 10, textTransform: 'none' }}>
          {t('earnings.settledTotal', { amt: usd(overview.settledEarnings) })}
          {pending > 0 && t('earnings.pendingMore', { amt: usd(pending) })}
          {t('earnings.onlyProvider')}
          {minUsd ? t('earnings.minSuffix', { amt: usd(minUsd) }) : ''}
          {t('earnings.periodEnd')}
          <br />可「划转到余额」把收益转成消费余额，立即用于调用市场 API（<b>转入后不可提现</b>）。
        </div>

        {showForm && (
          <div style={{ marginTop: 18, paddingTop: 18, borderTop: '1px solid var(--line)', maxWidth: 560 }}>
            {!confirming ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <Field label={t('earnings.form.amountLabel')}>
                  <div className="row" style={{ gap: 8 }}>
                    <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" placeholder="0.00"
                      style={{ ...inp, borderColor: amountErr ? 'var(--err)' : 'var(--line)' }} />
                    <button className="btn btn-sm" onClick={() => setAmount(String(withdrawable))}>{t('earnings.form.all')}</button>
                  </div>
                  <div className="micro-mono" style={{ fontSize: 11, marginTop: 6, textTransform: 'none', color: amountErr ? 'var(--err)' : 'var(--fg-4)' }}>
                    {amountErr || (t('earnings.form.amountHint', { amt: usd(withdrawable) }) + (minUsd ? t('earnings.minSuffix', { amt: usd(minUsd) }) : '') + (maxUsd ? t('earnings.maxSuffix', { amt: usd(maxUsd) }) : ''))}
                  </div>
                </Field>
                <Field label={t('earnings.form.addrLabel')}>
                  <input value={toAddress} onChange={(e) => setToAddress(e.target.value)} placeholder="0x…" className="mono"
                    style={{ ...inp, textTransform: 'none', borderColor: (addrTrim && !addrValid) ? 'var(--err)' : (addrValid ? 'rgba(95,208,168,0.6)' : 'var(--line)') }} />
                  {addrTrim === '' ? (
                    <div className="micro-mono" style={{ fontSize: 11, marginTop: 6, color: 'var(--fg-4)', textTransform: 'none' }}>{t('earnings.form.addrHintEmpty', { network: networkLabel })}</div>
                  ) : addrValid ? (
                    <div className="micro-mono" style={{ fontSize: 11, marginTop: 6, color: 'var(--c-mint)', textTransform: 'none', wordBreak: 'break-all' }}>{t('earnings.form.addrValid', { addr: toChecksum(addrTrim) })}</div>
                  ) : (
                    <div className="micro-mono" style={{ fontSize: 11, marginTop: 6, color: 'var(--err)', textTransform: 'none' }}>{t('earnings.form.addrInvalid')}</div>
                  )}
                </Field>
                <div className="row" style={{ gap: 8 }}>
                  <button className="btn btn-primary" disabled={!canConfirm} onClick={goConfirm}>{t('earnings.form.next')}</button>
                  <button className="btn" onClick={() => setShowForm(false)}>{t('earnings.form.cancel')}</button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="micro-mono" style={{ fontSize: 11, color: 'var(--fg-4)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{t('earnings.confirm.title')}</div>
                <div className="card" style={{ padding: 16, background: 'var(--bg-2)', display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '10px 16px', alignItems: 'baseline' }}>
                  <span style={{ color: 'var(--fg-4)', fontSize: 12 }}>{t('earnings.confirm.amount')}</span>
                  <span className="mono mint-text" style={{ fontSize: 20, fontWeight: 700 }}>{usd(amtNum)} <span style={{ fontSize: 12, color: 'var(--fg-4)' }}>USDC</span></span>
                  <span style={{ color: 'var(--fg-4)', fontSize: 12 }}>{t('earnings.confirm.toAddress')}</span>
                  <span className="mono" style={{ fontSize: 13, color: 'var(--fg-0)', wordBreak: 'break-all', textTransform: 'none', padding: '6px 8px', background: 'var(--bg-1)', borderRadius: 4, border: '1px solid var(--line)' }}>{toChecksum(addrTrim)}</span>
                  <span style={{ color: 'var(--fg-4)', fontSize: 12 }}>{t('earnings.confirm.network')}</span>
                  <span className="mono" style={{ fontSize: 13, color: 'var(--fg-1)', textTransform: 'none' }}>{networkLabel} · USDC</span>
                  <span style={{ color: 'var(--fg-4)', fontSize: 12 }}>{t('earnings.confirm.remaining')}</span>
                  <span className="mono" style={{ fontSize: 13, color: 'var(--fg-1)' }}>{usd(Math.max(0, withdrawable - amtNum))}</span>
                </div>
                <div className="micro" style={{ fontSize: 11.5, lineHeight: 1.6, textTransform: 'none', color: '#e0a93f' }}>
                  {t('earnings.confirm.warning')}
                </div>
                <div className="row" style={{ gap: 8 }}>
                  <button className="btn btn-primary" disabled={submitting} onClick={confirmSubmit}>{submitting ? t('earnings.confirm.submitting') : t('earnings.confirm.submit')}</button>
                  <button className="btn" disabled={submitting} onClick={() => setConfirming(false)}>{t('earnings.confirm.back')}</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ===== 划转到消费余额子面板（earnings → balance，单向不可逆）===== */}
        {tferShow && (
          <div style={{ marginTop: 18, paddingTop: 18, borderTop: '1px solid var(--line)', maxWidth: 560 }}>
            {!tferConfirming ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <Field label="划转金额 (USD)">
                  <div className="row" style={{ gap: 8 }}>
                    <input value={tferAmount} onChange={(e) => setTferAmount(e.target.value)} type="number" placeholder="0.00"
                      style={{ ...inp, borderColor: tferErr ? 'var(--err)' : 'var(--line)' }} />
                    <button className="btn btn-sm" onClick={() => setTferAmount(String(withdrawable))}>全部</button>
                  </div>
                  <div className="micro-mono" style={{ fontSize: 11, marginTop: 6, textTransform: 'none', color: tferErr ? 'var(--err)' : 'var(--fg-4)' }}>
                    {tferErr || `可划转 ${usd(withdrawable)}${tferMin ? ` · 单笔最低 ${usd(tferMin)}` : ''}`}
                  </div>
                </Field>
                <div className="micro" style={{ fontSize: 11.5, lineHeight: 1.6, textTransform: 'none', color: '#e0a93f' }}>
                  划转到消费余额后可直接用于调用市场里的 API，实时到账。注意：消费余额<b>不可提现</b>，此操作<b>不可逆</b>。
                </div>
                <div className="row" style={{ gap: 8 }}>
                  <button className="btn btn-primary" disabled={!tferCanConfirm} onClick={tferGoConfirm}>下一步：核对</button>
                  <button className="btn" onClick={() => setTferShow(false)}>取消</button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="micro-mono" style={{ fontSize: 11, color: 'var(--fg-4)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>请核对划转信息</div>
                <div className="card" style={{ padding: 16, background: 'var(--bg-2)', display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '10px 16px', alignItems: 'baseline' }}>
                  <span style={{ color: 'var(--fg-4)', fontSize: 12 }}>划转金额</span>
                  <span className="mono mint-text" style={{ fontSize: 20, fontWeight: 700 }}>{usd(tferAmtNum)} <span style={{ fontSize: 12, color: 'var(--fg-4)' }}>→ 消费余额</span></span>
                  <span style={{ color: 'var(--fg-4)', fontSize: 12 }}>划转后剩余可提现</span>
                  <span className="mono" style={{ fontSize: 13, color: 'var(--fg-1)' }}>{usd(Math.max(0, withdrawable - tferAmtNum))}</span>
                </div>
                <div className="micro" style={{ fontSize: 11.5, lineHeight: 1.6, textTransform: 'none', color: '#e0a93f' }}>
                  ⚠️ 划转后这部分资金转为消费余额，<b>不可再提现</b>，仅可用于调用 API。此操作不可逆。
                </div>
                <div className="row" style={{ gap: 8 }}>
                  <button className="btn btn-primary" disabled={tferSubmitting} onClick={tferSubmit}>{tferSubmitting ? '划转中…' : '确认划转'}</button>
                  <button className="btn" disabled={tferSubmitting} onClick={() => setTferConfirming(false)}>返回修改</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ===== tabs 只划分数据信息 ===== */}
      <div className="row" style={{ gap: 0, borderBottom: '1px solid var(--line)', marginBottom: 20 }}>
        <TabBtn active={tab === 'detail'} onClick={() => setTab('detail')}>{t('earnings.tab.detail')}{earnings?.total ? ` (${earnings.total})` : ''}</TabBtn>
        <TabBtn active={tab === 'records'} onClick={() => setTab('records')}>{t('earnings.tab.records')}{items.length ? ` (${items.length})` : ''}</TabBtn>
      </div>

      {tab === 'detail' && <EarningsTab overview={overview} earnings={earnings} feePct={feePct} pending={pending} />}
      {tab === 'records' && <RecordsTab items={items} cancel={cancel} />}
    </Shell>
  );
};

// ---------------- 收益明细 tab：概览 KPI + 每笔分成 ----------------
const EarningsTab = ({ overview, earnings, feePct, pending }) => {
  const { t } = useT();
  return (
  <>
    <div className="card" style={{ padding: 20, marginBottom: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 16 }}>
        <Stat label={t('earnings.stat.monthly')} value={usd(overview.monthlyNetEarnings)} accent />
        <Stat label={t('earnings.stat.pending')} value={usd(pending)} hint={pending > 0 ? t('earnings.stat.pendingHint') : undefined} />
        <Stat label={t('earnings.stat.totalCalls')} value={(overview.totalCalls || 0).toLocaleString()} />
        <Stat label={t('earnings.stat.activeCallers')} value={(overview.uniqueCallers || 0).toLocaleString()} />
      </div>
      <div className="micro" style={{ color: 'var(--fg-4)', fontSize: 11, marginTop: 14, textTransform: 'none' }}>
        {t('earnings.detailNote', { pct: feePct })}
      </div>
    </div>

    {(!earnings || earnings.items.length === 0) ? (
      <div style={{ color: 'var(--fg-4)', fontSize: 13, padding: '16px 0' }}>
        {t('earnings.noEarnings')}
      </div>
    ) : (
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="row earn-row" style={{ padding: '9px 16px', borderBottom: '1px solid var(--line)', color: 'var(--fg-4)', fontSize: 11 }}>
          <span style={{ flex: 2, minWidth: 0 }}>{t('earnings.col.api')}</span>
          <span style={{ flex: 1, textAlign: 'right' }}>{t('earnings.col.gross')}</span>
          <span style={{ flex: 1, textAlign: 'right' }}>{t('earnings.col.fee')}</span>
          <span style={{ flex: 1, textAlign: 'right' }}>{t('earnings.col.net')}</span>
          <span style={{ width: 64, textAlign: 'right' }}>{t('earnings.col.status')}</span>
          <span style={{ width: 84, textAlign: 'right' }}>{t('earnings.col.time')}</span>
        </div>
        {earnings.items.map((e, i) => (
          <div key={e.id} className="row earn-row" style={{ padding: '11px 16px', alignItems: 'center', borderBottom: i === earnings.items.length - 1 ? 'none' : '1px solid var(--line)', fontSize: 13 }}>
            <span style={{ flex: 2, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--fg-1)' }}>{e.apiName}</span>
            <span className="mono" style={{ flex: 1, textAlign: 'right', color: 'var(--fg-3)' }}>{usdSmart(e.grossAmount)}</span>
            <span className="mono" style={{ flex: 1, textAlign: 'right', color: 'var(--fg-4)' }}>−{usdSmart(e.platformFee)}</span>
            <span className="mono" style={{ flex: 1, textAlign: 'right', fontWeight: 600, color: 'var(--fg-0)' }}>{usdSmart(e.netAmount)}</span>
            <span style={{ width: 64, textAlign: 'right' }}><Pill map={EARN_STATUS} status={e.status} /></span>
            <span className="micro-mono" style={{ width: 84, textAlign: 'right', color: 'var(--fg-4)', fontSize: 11, textTransform: 'none' }}>{fmtDate(e.createdAt)}</span>
          </div>
        ))}
        <div className="row earn-row" style={{ padding: '10px 16px', borderTop: '1px solid var(--line)', background: 'var(--bg-2)', fontSize: 12 }}>
          <span style={{ flex: 2, color: 'var(--fg-4)' }}>{t('earnings.pageTotal')}</span>
          <span className="mono" style={{ flex: 1, textAlign: 'right', color: 'var(--fg-3)' }}>{usdSmart(earnings.totalGross)}</span>
          <span className="mono" style={{ flex: 1, textAlign: 'right', color: 'var(--fg-4)' }}>−{usdSmart(earnings.totalFees)}</span>
          <span className="mono mint-text" style={{ flex: 1, textAlign: 'right', fontWeight: 600 }}>{usdSmart(earnings.totalNet)}</span>
          <span style={{ width: 64 }} /><span style={{ width: 84 }} />
        </div>
      </div>
    )}
  </>
  );
};

// ---------------- 提现记录 tab ----------------
const RecordsTab = ({ items, cancel }) => {
  const { t } = useT();
  return (
  items.length === 0 ? (
    <div style={{ color: 'var(--fg-4)', fontSize: 13, padding: '12px 0' }}>{t('earnings.noWithdrawals')}</div>
  ) : (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {items.map((w, i) => (
        <div key={w.id} className="row" style={{ padding: '13px 16px', alignItems: 'center', justifyContent: 'space-between', gap: 12, borderBottom: i === items.length - 1 ? 'none' : '1px solid var(--line)', fontSize: 13 }}>
          <div style={{ minWidth: 0 }}>
            <span className="mono" style={{ fontWeight: 600, color: 'var(--fg-0)' }}>{usd(w.requestedAmountUsd)}</span>
            <span className="micro-mono" style={{ color: 'var(--fg-4)', marginLeft: 10, fontSize: 11, textTransform: 'none' }}>→ {short(w.toAddress)}</span>
            {w.txHash && (
              <a href={scanUrl(w.network, w.txHash)} target="_blank" rel="noreferrer" className="mono" style={{ color: 'var(--c-mint)', marginLeft: 10, fontSize: 11 }}>{short(w.txHash, 8)} ↗</a>
            )}
            {w.status === 'REQUESTED' && w.failReason && (
              <div className="micro" style={{ color: '#e0a93f', fontSize: 10.5, marginTop: 3, textTransform: 'none' }}>{t('earnings.record.failedRequeued', { reason: w.failReason })}</div>
            )}
            {w.status === 'REJECTED' && (
              <div className="micro" style={{ color: 'var(--err, #f55)', fontSize: 10.5, marginTop: 3, textTransform: 'none' }}>{t('earnings.record.rejectReason', { reason: w.reviewNote || t('earnings.record.rejectNoNote') })}</div>
            )}
            {w.status === 'FAILED' && w.failReason && (
              <div className="micro" style={{ color: 'var(--err, #f55)', fontSize: 10.5, marginTop: 3, textTransform: 'none' }}>{t('earnings.record.failReason', { reason: w.failReason })}</div>
            )}
          </div>
          <div className="row" style={{ gap: 10, alignItems: 'center', flexShrink: 0 }}>
            <Pill map={WD_STATUS} status={w.status} />
            {w.status === 'REQUESTED' && (
              <button className="btn btn-sm" style={{ color: 'var(--err)', borderColor: 'var(--err)' }} onClick={() => cancel(w.id)}>{t('earnings.record.cancel')}</button>
            )}
          </div>
        </div>
      ))}
    </div>
  )
  );
};

const inp = { flex: 1, padding: '9px 12px', fontSize: 14, borderRadius: 6, background: 'var(--bg-1)', color: 'var(--fg-1)', border: '1px solid var(--line)', outline: 'none' };

const TabBtn = ({ active, onClick, children }) => (
  <button onClick={onClick} style={{
    padding: '10px 2px', marginRight: 24, background: 'none', border: 'none', cursor: 'pointer',
    fontSize: 14, fontWeight: active ? 600 : 400, color: active ? 'var(--fg-0)' : 'var(--fg-3)',
    borderBottom: `2px solid ${active ? 'var(--c-mint, #5fd0a8)' : 'transparent'}`, marginBottom: -1,
  }}>{children}</button>
);

const Stat = ({ label, value, hint, accent }) => (
  <div>
    <div className="micro-mono" style={{ color: 'var(--fg-4)', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
    <div className={'mono' + (accent ? ' mint-text' : '')} style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2, marginTop: 3, color: accent ? undefined : 'var(--fg-0)' }}>{value}</div>
    {hint && <div className="micro" style={{ color: 'var(--fg-4)', fontSize: 10, marginTop: 2, textTransform: 'none' }}>{hint}</div>}
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <div className="micro-mono" style={{ color: 'var(--fg-4)', fontSize: 11, marginBottom: 6 }}>{label}</div>
    {children}
  </div>
);

const Shell = ({ children }) => {
  const { t } = useT();
  return (
  <section style={{ maxWidth: 820, margin: '0 auto', padding: '40px 24px 80px' }}>
    <h1 style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.02em', margin: '0 0 24px' }}>{t('earnings.title')}</h1>
    {children}
  </section>
  );
};

const Gate = ({ title, desc, cta, onClick }) => (
  <div className="card" style={{ padding: 40, textAlign: 'center' }}>
    <div style={{ fontSize: 16, fontWeight: 600, marginBottom: desc ? 8 : 16 }}>{title}</div>
    {desc && <div style={{ color: 'var(--fg-3)', fontSize: 13, marginBottom: 16, lineHeight: 1.6 }}>{desc}</div>}
    <button className="btn btn-primary" onClick={onClick}>{cta}</button>
  </div>
);
